import { createResource } from "~/lib/resourceFactory";
import {
  getBankTransaction,
  listBankTransactionCategories,
  listBankTransactions,
} from "./bank-transaction.api";

type BankTransactionsPage = Awaited<ReturnType<typeof listBankTransactions>>;
type BankTransactionDetail = Awaited<ReturnType<typeof getBankTransaction>>;

export type BankTransactionsParams = {
  businessId: string;
  search?: string;
  offset?: number;
  limit: number;
  ids?: string[];
  status?: "pending" | "approved" | "skipped";
  sortBy?: string;
  sortDir?: "asc" | "desc";
  /** Facet filter: only transactions whose category is in this list. */
  category?: string[];
  /** Only transactions for these bank accounts. */
  bankAccountIds?: string[];
};

export type BankTransactionDetailParams = {
  businessId: string;
  id: string;
};

export const BankTransactionResource = createResource<
  BankTransactionsPage,
  BankTransactionsParams
>({
  key: (p) =>
    [
      "bank-transaction",
      p.businessId,
      p.limit,
      p.search ?? "",
      p.offset ?? 0,
      p.status ?? "",
      p.sortBy ?? "",
      p.sortDir ?? "",
      p.category === undefined ? "*" : p.category.join(","),
      p.bankAccountIds === undefined ? "*" : p.bankAccountIds.join(","),
      p.ids === undefined ? "*" : p.ids.join(","),
    ] as const,
  fetcher: (p) =>
    listBankTransactions({
      data: {
        businessId: p.businessId,
        search: p.search,
        offset: p.offset,
        limit: p.limit,
        ids: p.ids,
        status: p.status,
        sortBy: p.sortBy,
        sortDir: p.sortDir,
        category: p.category,
        bankAccountIds: p.bankAccountIds,
      },
    }),
});

/**
 * Empty placeholder used when the bank-transaction detail query is disabled
 * (e.g. a non-bank journal entry that never reads this query, or the "new"
 * transaction route). Without placeholderData a disabled query has
 * `data === undefined`, which makes solid-query suspend the nearest Suspense
 * boundary during SSR — keeping the SSR stream open and the browser tab
 * spinning forever on hard refresh.
 */
export const EMPTY_BANK_TRANSACTION: BankTransactionDetail = {
  id: "",
  bankConnectionId: null,
  bankConnectionAccountId: "",
  businessId: "",
  provider: "",
  providerTransactionId: "",
  date: new Date(0),
  name: "",
  merchantName: undefined,
  amount: 0,
  currencyCode: "USD",
  category: undefined,
  providerCategoryId: undefined,
  pending: false,
  status: "pending",
  mappedAccountId: undefined,
  mappedAccountCode: undefined,
  mappedAccountName: undefined,
  reviewedAt: undefined,
  postedJournalEntryId: undefined,
  lines: [],
  institutionName: undefined,
  bankAccountName: undefined,
  bankAccountMask: undefined,
  bankAccountChartAccountId: undefined,
  createdAt: new Date(0),
  updatedAt: new Date(0),
  unofficialCurrencyCode: undefined,
  merchantCategoryCode: undefined,
  runningBalance: undefined,
  paymentChannel: undefined,
  authorizedDate: undefined,
  authorizedDatetime: undefined,
  postedDatetime: undefined,
  logoUrl: undefined,
  website: undefined,
  merchantEntityId: undefined,
  pfcPrimary: undefined,
  pfcDetailed: undefined,
  pfcConfidence: undefined,
  pfcIconUrl: undefined,
  bfcPrimary: undefined,
  bfcDetailed: undefined,
  bfcConfidence: undefined,
  transactionCode: undefined,
  transactionType: undefined,
  checkNumber: undefined,
  pendingTransactionId: undefined,
  accountOwner: undefined,
  originalDescription: undefined,
  counterparties: undefined,
  paymentMeta: undefined,
  locationAddress: undefined,
  locationCity: undefined,
  locationRegion: undefined,
  locationPostalCode: undefined,
  locationCountry: undefined,
  locationLat: undefined,
  locationLon: undefined,
  locationStoreNumber: undefined,
  addressLat: undefined,
  addressLon: undefined,
};

export const BankTransactionDetailResource = createResource<
  BankTransactionDetail,
  BankTransactionDetailParams
>({
  key: ({ businessId, id }) => ["bank-transaction", businessId, id] as const,
  fetcher: ({ businessId, id }) =>
    getBankTransaction({ data: { businessId, id } }),
  // Avoid suspending SSR when the query is disabled (see EMPTY_BANK_TRANSACTION).
  placeholderData: EMPTY_BANK_TRANSACTION,
});

type BankTransactionCategoryFacets = Awaited<
  ReturnType<typeof listBankTransactionCategories>
>;

/** Distinct categories + counts for the category facet filter. */
export const BankTransactionCategoriesResource = createResource<
  BankTransactionCategoryFacets,
  { businessId: string }
>({
  key: ({ businessId }) => ["bank-transaction-categories", businessId] as const,
  fetcher: ({ businessId }) =>
    listBankTransactionCategories({ data: { businessId } }),
});
